import React, { useState, useEffect } from 'react';
import { Search, Grid, List, RefreshCw, MapPin, Calendar, TrendingUp, Users, Target, CheckCircle, XCircle, Crop } from 'lucide-react';
import './PreSowing.css';

const CropsList = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({
    totalActivities: 104,
    landPrepared: 104,
    notPrepared: 0,
    activeFarmers: 104,
    plannedArea: 14791.5
  });

  useEffect(() => {
    // Fetch land preparation data
    fetch('/api/land-preparations/')
      .then(res => res.json())
      .then(data => {
        setActivities(data.results || data);
      })
      .catch(err => console.error('Error fetching activities:', err));
  }, []);

  const mockActivities = [
    {
      id: 1,
      crop: 'Wheat',
      farmer: 'allah_yaar_Farm_001',
      location: 'allah_yaar',
      status: 'Prepared',
      activity: 'Land Preparation',
      variety: 'Dikash',
      avgYield: '35 units',
      preparationDate: 'Jan 28, 2026'
    },
    {
      id: 2,
      crop: 'Wheat',
      farmer: 'bilal_sadiqui_Farm_001',
      location: 'bilal_sadiqui',
      status: 'Prepared',
      activity: 'Land Preparation',
      variety: 'Dikash',
      avgYield: '1 units',
      preparationDate: 'Jan 28, 2026'
    },
    {
      id: 3,
      crop: 'Wheat',
      farmer: 'm.sadiq_Farm_001',
      location: 'm.sadiq',
      status: 'Prepared',
      activity: 'Land Preparation',
      variety: 'Dikash Akbar',
      avgYield: '39 units',
      preparationDate: 'Jan 28, 2026'
    }
  ];

  const displayActivities = activities.length > 0 ? activities : mockActivities;

  return (
    <div className="pre-sowing">
      {/* Stage Tabs */}
      <div className="stage-tabs">
        <button className="stage-tab active">Pre-Sowing</button>
        <button className="stage-tab">Sowing & Emergence</button>
        <button className="stage-tab">Emergence</button>
        <button className="stage-tab">Growth & Tillering</button>
        <button className="stage-tab">Booting Stage</button>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Farm Activities Dashboard</h1>
          <p className="subtitle">Monitor and analyze all farm preparation activities across fields</p>
        </div>
        <button className="refresh-btn">
          <RefreshCw size={16} />
          Refresh Data
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalActivities}</div>
            <div className="stat-label">Total Activities</div>
            <div className="stat-sublabel">{stats.totalActivities} fields</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.landPrepared}</div>
            <div className="stat-label">Land Prepared</div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red">
            <XCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value danger">{stats.notPrepared}</div>
            <div className="stat-label">Not Prepared</div>
            <div className="stat-sublabel danger">Requires attention</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeFarmers}</div>
            <div className="stat-label">Active Farmers</div>
            <div className="stat-sublabel">Managing fields</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <Target size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.plannedArea} ha</div>
            <div className="stat-label">Planned Area</div>
            <div className="stat-sublabel">This season</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="controls-section">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search activities, crops, fields, or farmers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select className="field-filter">
          <option>All Fields</option>
          <option>Field 1</option>
          <option>Field 2</option>
        </select>

        <div className="view-toggle">
          <button 
            className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <Grid size={18} />
          </button>
          <button 
            className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <List size={18} />
          </button>
        </div>

        <div className="status-filters">
          <button 
            className={`filter-chip ${filterStatus === 'All' ? 'active' : ''}`}
            onClick={() => setFilterStatus('All')}
          >
            All
          </button>
          <button 
            className={`filter-chip ${filterStatus === 'Prepared' ? 'active' : ''}`}
            onClick={() => setFilterStatus('Prepared')}
          >
            Prepared
          </button>
          <button 
            className={`filter-chip ${filterStatus === 'Not Prepared' ? 'active' : ''}`}
            onClick={() => setFilterStatus('Not Prepared')}
          >
            Not Prepared
          </button>
        </div>
      </div>

      {/* Results Count and Sorting */}
      <div className="results-bar">
        <div className="results-count">
          Showing <strong>{displayActivities.length}</strong> of <strong>{stats.totalActivities}</strong> activities
        </div>
        <div className="sort-options">
          <span>Sort by:</span>
          <button className="sort-btn">Crop ↑↓</button>
          <button className="sort-btn">Date ↑↓</button>
          <button className="sort-btn">Status ↑↓</button>
        </div>
      </div>

      {/* Activities Grid */}
      <div className={`activities-${viewMode}`}>
        {displayActivities.map((activity) => (
          <div key={activity.id} className="activity-card">
            <div className="activity-header">
              <div className="activity-crop">
                <span className="crop-icon">🌾</span>
                <span className="crop-name">{activity.crop}</span>
              </div>
              <span className={`status-badge ${activity.status.toLowerCase().replace(' ', '-')}`}>
                {activity.status}
              </span>
            </div>

            <div className="activity-location">
              <MapPin size={14} />
              <span>{activity.farmer} • {activity.location}</span>
            </div>

            <div className="activity-type">
              {activity.activity}
            </div>

            <div className="activity-details">
              <div className="detail-row">
                <span className="detail-label">Variety</span>
                <span className="detail-value">{activity.variety}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Avg Yield</span>
                <span className="detail-value">{activity.avgYield}</span>
              </div>
            </div>

            <div className="activity-footer">
              <div className="activity-date">
                <Calendar size={14} />
                <span>Preparation</span>
                <strong>{activity.preparationDate}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CropsList;
